// Minimal in-app i18n. When professional translations land, swap to a real lib.
// Source of truth keys live here so we never hardcode UI text in components.

export type Language = "en" | "si" | "ta";

type Strings = Record<string, Record<Language, string>>;

export const STRINGS: Strings = {
  "app.title": { en: "ClearBooks", si: "ClearBooks", ta: "ClearBooks" },
  "app.tagline": {
    en: "Books, ready.",
    si: "ගිණුම්, සූදානම්.",
    ta: "கணக்குகள், தயாராக.",
  },
  "nav.dashboard": { en: "Dashboard", si: "මුල් පිටුව", ta: "முகப்பு" },
  "nav.invoices": { en: "Invoices", si: "ඉන්වොයිස්", ta: "விலைப்பட்டியல்" },
  "nav.bills": { en: "Bills", si: "බිල්පත්", ta: "பில்கள்" },
  "nav.bank": { en: "Bank", si: "බැංකුව", ta: "வங்கி" },
  "nav.payroll": { en: "Payroll", si: "වැටුප්", ta: "ஊதியம்" },
  "nav.reports": { en: "Reports", si: "වාර්තා", ta: "அறிக்கைகள்" },
  "nav.settings": { en: "Settings", si: "සැකසුම්", ta: "அமைப்புகள்" },
  "nav.team": { en: "Team", si: "කණ්ඩායම", ta: "குழு" },
  "auth.signIn": { en: "Sign in", si: "පුරන්න", ta: "உள்நுழை" },
  "auth.signUp": { en: "Create account", si: "ගිණුමක් සාදන්න", ta: "கணக்கு உருவாக்கு" },
  "auth.email": { en: "Email", si: "ඊමේල්", ta: "மின்னஞ்சல்" },
  "auth.password": { en: "Password", si: "මුරපදය", ta: "கடவுச்சொல்" },
  "auth.name": { en: "Your name", si: "ඔබේ නම", ta: "உங்கள் பெயர்" },
  "auth.businessName": {
    en: "Business name",
    si: "ව්‍යාපාරයේ නම",
    ta: "வணிகப் பெயர்",
  },
  "auth.haveAccount": {
    en: "Have an account?",
    si: "ගිණුමක් තිබේද?",
    ta: "ஏற்கனவே கணக்கு உள்ளதா?",
  },
  "auth.noAccount": {
    en: "New to ClearBooks?",
    si: "ClearBooks හි අලුත්ද?",
    ta: "ClearBooks-க்கு புதியவரா?",
  },
  "auth.signOut": { en: "Sign out", si: "ඉවත් වන්න", ta: "வெளியேறு" },
  "common.loading": { en: "Loading…", si: "පූරණය වෙමින්…", ta: "ஏற்றுகிறது…" },
  "lang.en": { en: "English", si: "English", ta: "English" },
  "lang.si": { en: "සිංහල", si: "සිංහල", ta: "සිංහල" },
  "lang.ta": { en: "தமிழ்", si: "தமிழ்", ta: "தமிழ்" },
  "dash.welcome": {
    en: "Welcome back",
    si: "සාදරයෙන් පිළිගනිමු",
    ta: "மீண்டும் வரவேற்கிறோம்",
  },
  "dash.placeholder": {
    en: "Foundation laid. Modules ship from week 2.",
    si: "පදනම සැකසේ. මොඩියුල සති දෙකේ සිට.",
    ta: "அடித்தளம் தயார். தொகுதிகள் வாரம் 2 முதல்.",
  },
  // ─── Export customer (B1) ───
  "customer.isExport": {
    en: "Export / non-resident customer",
    si: "අපනයන / නේවාසික නොවන පාරිභෝගිකයා",
    ta: "ஏற்றுமதி / குடியுரிமை இல்லாத வாடிக்கையாளர்",
  },
  "customer.isExport.hint": {
    en: "Supplies are zero-rated for VAT and outside SSCL. Invoices to this customer cannot carry local VAT or SSCL — classify lines as zero-rated.",
    si: "සැපයුම් VAT සඳහා ශුන්‍ය අනුපාතිකයි, SSCL වලින් පිටතයි. මෙම පාරිභෝගිකයාට ඉන්වොයිස් වල දේශීය VAT හෝ SSCL තිබිය නොහැක — පේළි ශුන්‍ය-අනුපාත ලෙස වර්ගීකරණය කරන්න.",
    ta: "வழங்கல்கள் VAT-க்கு பூஜ்ய விகிதம், SSCL-க்கு வெளியே. இந்த வாடிக்கையாளருக்கான விலைப்பட்டியல்களில் உள்ளூர் VAT அல்லது SSCL இருக்க முடியாது — வரிகளை பூஜ்ய விகிதமாக வகைப்படுத்தவும்.",
  },
  "invoice.exportGuardError": {
    en: "This customer is marked export/non-resident — invoices can't carry VAT or SSCL. Set the lines to zero-rated.",
    si: "මෙම පාරිභෝගිකයා අපනයන/නේවාසික නොවන ලෙස සලකුණු කර ඇත — ඉන්වොයිස් වල VAT හෝ SSCL තිබිය නොහැක. පේළි ශුන්‍ය-අනුපාත ලෙස සකසන්න.",
    ta: "இந்த வாடிக்கையாளர் ஏற்றுமதி/குடியுரிமையற்றவர் எனக் குறிக்கப்பட்டுள்ளார் — விலைப்பட்டியல்களில் VAT அல்லது SSCL இருக்க முடியாது. வரிகளை பூஜ்ய விகிதமாக அமைக்கவும்.",
  },
  // ─── VAT reconciliation (C3) ───
  "reports.tab.vatRecon": {
    en: "VAT Recon",
    si: "VAT සැසඳීම",
    ta: "VAT ஒப்பீடு",
  },
  "vatRecon.title": {
    en: "VAT Reconciliation",
    si: "VAT සැසඳීම",
    ta: "VAT ஒப்பீடு",
  },
  "vatRecon.reconciled": {
    en: "Reconciled — every VAT control account ties to the return.",
    si: "සැසඳී ඇත — සෑම VAT පාලන ගිණුමක්ම ප්‍රතිලාභයට ගැළපේ.",
    ta: "ஒப்பிடப்பட்டது — ஒவ்வொரு VAT கட்டுப்பாட்டுக் கணக்கும் வருமானத்துடன் பொருந்துகிறது.",
  },
  "vatRecon.notReconciled": {
    en: "Not reconciled — one or more VAT control accounts disagree with the return. See the highlighted rows.",
    si: "සැසඳී නැත — VAT පාලන ගිණුම් එකක් හෝ කිහිපයක් ප්‍රතිලාභයට නොගැළපේ. උද්දීපිත පේළි බලන්න.",
    ta: "ஒப்பிடப்படவில்லை — ஒன்று அல்லது அதற்கு மேற்பட்ட VAT கட்டுப்பாட்டுக் கணக்குகள் வருமானத்துடன் முரண்படுகின்றன. முன்னிலைப்படுத்தப்பட்ட வரிசைகளைப் பார்க்கவும்.",
  },
  "vatRecon.col.account": {
    en: "Control account",
    si: "පාලන ගිණුම",
    ta: "கட்டுப்பாட்டுக் கணக்கு",
  },
  "vatRecon.col.cage": { en: "Cage", si: "කොටුව", ta: "கூடு" },
  "vatRecon.col.return": { en: "Return (LKR)", si: "ප්‍රතිලාභය (රු.)", ta: "வருமானம் (ரூ.)" },
  "vatRecon.col.ledger": { en: "Ledger (LKR)", si: "ලෙජර් (රු.)", ta: "லெட்ஜர் (ரூ.)" },
  "vatRecon.col.difference": { en: "Difference", si: "වෙනස", ta: "வேறுபாடு" },
};

export function t(key: string, lang: Language): string {
  return STRINGS[key]?.[lang] ?? STRINGS[key]?.en ?? key;
}
